import { APIResponse } from "../../../models/NetworkResponse";
import {
  SwayyCatalogOption,
  SwayyOptionDisplayType,
  SwayyOptionValue,
} from "../models/options/SwayyCatalogOption";
import {
  SwayyCreateOptionPayload,
  SwayyCreateOptionValuePayload,
  SwayyUpdateOptionPayload,
  SwayyUpdateOptionValuePayload,
} from "../models/options/SwayyOptionPayloads";
import { deleteAPI, getAPI, patchAPI, postAPI } from "./Networking";
import {
  mapSwayyEligibleOption,
  mapSwayyOptionValue,
} from "./SwayyProductsAPI";
import {
  asBool,
  assertSuccess,
  getResponseData,
  pickNumber,
  requireRowArray,
  SwayyApiError,
  SwayyAPIResponse,
} from "./swayyApiHelpers";

const BASE = "/v1/catalog/admin/options";

export const mapSwayyCatalogOption = (
  raw: unknown,
): SwayyCatalogOption | null => {
  const eligible = mapSwayyEligibleOption(raw);
  if (!eligible) return null;
  const row = (raw ?? {}) as Record<string, unknown>;
  return {
    id: eligible.id,
    name: eligible.name,
    displayType: eligible.displayType,
    position: pickNumber(row.position) ?? 0,
    isActive: asBool(row.is_active ?? row.active, true),
    values: eligible.values,
  };
};

export const listSwayyCatalogOptions = async (
  signal?: AbortSignal,
): Promise<SwayyCatalogOption[]> => {
  const response = await getAPI<SwayyAPIResponse<unknown>>(
    BASE,
    undefined,
    signal,
  );
  assertSuccess(response);
  return requireRowArray(getResponseData(response))
    .map(mapSwayyCatalogOption)
    .filter((option): option is SwayyCatalogOption => option !== null);
};

export const getSwayyCatalogOption = async (
  optionId: number,
  signal?: AbortSignal,
): Promise<SwayyCatalogOption> => {
  const response = await getAPI<SwayyAPIResponse<unknown>>(
    `${BASE}/${optionId}`,
    undefined,
    signal,
  );
  assertSuccess(response);
  const mapped = mapSwayyCatalogOption(getResponseData(response));
  if (!mapped) throw new SwayyApiError("Option not found.");
  return mapped;
};

export const createSwayyCatalogOption = async (
  payload: SwayyCreateOptionPayload,
): Promise<SwayyCatalogOption> => {
  const response = await postAPI(BASE, payload);
  assertSuccess(response);
  const mapped = mapSwayyCatalogOption(
    (response as APIResponse & { data?: unknown }).data,
  );
  if (!mapped) {
    throw new SwayyApiError("Option was created but response was empty.");
  }
  return mapped;
};

export const updateSwayyCatalogOption = async (
  optionId: number,
  payload: SwayyUpdateOptionPayload,
): Promise<SwayyCatalogOption> => {
  const response = await patchAPI<SwayyAPIResponse<unknown>>(
    `${BASE}/${optionId}`,
    payload,
  );
  assertSuccess(response);
  const mapped = mapSwayyCatalogOption(getResponseData(response));
  if (!mapped) {
    throw new SwayyApiError("Option was updated but response was empty.");
  }
  return mapped;
};

export const createSwayyOptionValue = async (
  optionId: number,
  payload: SwayyCreateOptionValuePayload,
): Promise<SwayyOptionValue> => {
  const response = await postAPI(`${BASE}/${optionId}/values`, payload);
  assertSuccess(response);
  const data = (response as APIResponse & { data?: unknown }).data;
  const mapped =
    mapSwayyOptionValue(data) ||
    (Array.isArray(data) ? mapSwayyOptionValue(data[0]) : null);
  if (!mapped) {
    throw new SwayyApiError("Value was created but response was empty.");
  }
  return mapped;
};

export const updateSwayyOptionValue = async (
  optionId: number,
  valueId: number,
  payload: SwayyUpdateOptionValuePayload,
): Promise<SwayyOptionValue> => {
  const response = await patchAPI<SwayyAPIResponse<unknown>>(
    `${BASE}/${optionId}/values/${valueId}`,
    payload,
  );
  assertSuccess(response);
  const data = getResponseData(response);
  const nested =
    data && typeof data === "object"
      ? (data as { option_value?: unknown }).option_value
      : null;
  const mapped = mapSwayyOptionValue(nested ?? data);
  if (!mapped) {
    throw new SwayyApiError("Value was updated but response was empty.");
  }
  return mapped;
};

export const deleteSwayyOptionValue = async (
  optionId: number,
  valueId: number,
): Promise<void> => {
  const response = await deleteAPI<
    SwayyAPIResponse<unknown> | null | undefined | ""
  >(`${BASE}/${optionId}/values/${valueId}`);
  if (response == null || response === "") return;
  assertSuccess(response);
};

export const DISPLAY_TYPE_OPTIONS: Array<{
  id: SwayyOptionDisplayType;
  value: string;
}> = [
  { id: "dropdown", value: "Dropdown" },
  { id: "swatch", value: "Swatch" },
  { id: "button", value: "Button" },
  { id: "text", value: "Text" },
];
